class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.defaultSuggestionsHTML = null;
    this._focusOutTimer = null;

    const defaultNode = this.querySelector('[data-default-suggestions]');
    if (defaultNode) {
      this.defaultSuggestionsHTML = defaultNode.outerHTML;
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search') || this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    if (!this.input) return;

    this.clearButton = this.querySelector('.header__search-clear');

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
      this.toggleClearButton();
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // pointerdown runs before focus shift — reliable close on outside click
    this.onClickOutside = this.onClickOutside.bind(this);
    document.addEventListener('pointerdown', this.onClickOutside, true);

    if (this.clearButton) {
      this.clearButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.input.value = '';
        this.toggleClearButton();
        this.close(true);
        this.input.focus();
        this.showDefaultSuggestions();
      });
    }

    this.toggleClearButton();
  }

  isResultsPanelVisible() {
    return this.isOpen || this.hasAttribute('open') || this.hasAttribute('loading');
  }

  toggleClearButton() {
    if (!this.clearButton) return;
    if (this.getQuery().length) {
      this.clearButton.removeAttribute('hidden');
    } else {
      this.clearButton.setAttribute('hidden', '');
    }
  }

  showDefaultSuggestions() {
    if (!this.predictiveSearchResults) return;

    if (this.defaultSuggestionsHTML) {
      this.predictiveSearchResults.innerHTML = this.defaultSuggestionsHTML;
    }

    this.open();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.removeAttribute('loading');
      this.showDefaultSuggestions();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    if (this._focusOutTimer) {
      clearTimeout(this._focusOutTimer);
      this._focusOutTimer = null;
    }

    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showDefaultSuggestions();
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    if (this._focusOutTimer) clearTimeout(this._focusOutTimer);

    // Allow result/link mousedown to run before closing
    this._focusOutTimer = setTimeout(() => {
      this._focusOutTimer = null;
      if (this.contains(document.activeElement)) return;
      this.close();
    }, 150);
  }

  onClickOutside(event) {
    if (!this.isResultsPanelVisible()) return;
    if (this.contains(event.target)) return;

    this.close();
    if (this.input && document.activeElement === this.input) {
      this.input.blur();
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        this.input.blur();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const url =
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
      `&resources[type]=product,query,collection` +
      `&resources[limit]=8` +
      `&resources[limit_scope]=each` +
      `&section_id=predictive-search`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        if (resultsMarkup && resultsMarkup.trim().length > 0) {
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
        } else {
          this.close();
        }
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegionElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionElement) {
      this.setLiveRegionText(liveRegionElement.textContent);
    }
  }

  getResultsMaxHeight() {
    const header = document.getElementById('shopify-section-header');
    if (header) {
      this.resultsMaxHeight = window.innerHeight - header.getBoundingClientRect().bottom;
    } else {
      this.resultsMaxHeight = window.innerHeight - 100;
    }
    return this.resultsMaxHeight;
  }

  open() {
    const header = document.getElementById('shopify-section-header') || document.querySelector('.header');
    if (header) {
      const headerHeight = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);

      const updateHeaderBottom = () => {
        const rect = header.getBoundingClientRect();
        const bottom = Math.max(0, rect.bottom);
        document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
      };
      updateHeaderBottom();

      this._updateHeaderBottom = updateHeaderBottom;
      window.addEventListener('scroll', updateHeaderBottom, { passive: true });
      window.addEventListener('resize', updateHeaderBottom, { passive: true });
    }

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.predictiveSearchResults.style.display = 'block';
    }

    this.setAttribute('open', true);
    this.setAttribute('results', 'true');
    this.input.setAttribute('aria-expanded', true);

    document.body.classList.add('predictive-search-open');

    const modalOverlay = document.querySelector('.modal-overlay');
    if (modalOverlay) {
      modalOverlay.classList.add('active');
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (this._focusOutTimer) {
      clearTimeout(this._focusOutTimer);
      this._focusOutTimer = null;
    }

    if (this._updateHeaderBottom) {
      window.removeEventListener('scroll', this._updateHeaderBottom);
      window.removeEventListener('resize', this._updateHeaderBottom);
      this._updateHeaderBottom = null;
    }

    if (clearSearchTerm) {
      this.input.value = '';
      this.toggleClearButton();
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.removeAttribute('loading');
    // Keep results markup cached for re-open, but never leave panel forced-visible via results attr alone
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = 'none';
      this.predictiveSearchResults.style.maxHeight = '';
    }

    document.body.classList.remove('predictive-search-open');

    const modalOverlay = document.querySelector('.modal-overlay');
    if (modalOverlay) {
      modalOverlay.classList.remove('active');
    }

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
